import { PLAYBOOK_SCOPE_DESCRIPTION_MAX_LENGTH } from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { linkStyle } from "@repo/ui/Link/index";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { FieldDiffView } from "./fieldDiffView.js";

const DESCRIPTION =
  "Describe which customer interactions this playbook is for, such as the kind of call it covers and what it leaves out.";

const PLACEHOLDER =
  "Inbound sales calls from prospective customers. Excludes billing questions, support requests, and calls that end before the customer states a need.";

export interface ScopeSettingsFormProps {
  /** Trimmed and non-empty, or null when the playbook has no scope. */
  description: string | null;
  onUpdateDescription: (description: string | null) => void;
  isReadOnly: boolean;
  /**
   * Live/published description. When provided (i.e. not `undefined`) and it
   * differs from the current one, a "See changes" toggle diffs the draft
   * against it.
   */
  liveDescription?: string | null;
  /** Show a draft dot next to the label when there are unpublished changes. */
  hasUnpublishedChanges?: boolean;
}

function normalizeScopeDescription(text: string): string | null {
  const trimmedText = text.trim();
  return trimmedText === "" ? null : trimmedText;
}

type ScopeTab = "edit" | "diff";

export const ScopeSettingsForm: FC<ScopeSettingsFormProps> = (props) => {
  const {
    description,
    onUpdateDescription,
    isReadOnly,
    liveDescription,
    hasUnpublishedChanges,
  } = props;
  const [tab, setTab] = useState<ScopeTab>("edit");

  // The draft holds the trimmed description, so the text being typed lives
  // here: feeding the trimmed value back would eat a trailing space or newline
  // mid-sentence. It is replaced only when the description changes to
  // something the text doesn't already say (a discard, a delete, a reload).
  const [text, setText] = useState(description ?? "");
  const [syncedDescription, setSyncedDescription] = useState(description);
  if (description !== syncedDescription) {
    setSyncedDescription(description);
    if (normalizeScopeDescription(text) !== description) {
      setText(description ?? "");
    }
  }

  const handleTextChange = (newText: string) => {
    setText(newText);
    onUpdateDescription(normalizeScopeDescription(newText));
  };

  const showDiffToggle =
    liveDescription !== undefined && liveDescription !== description;
  // Once the draft matches live again (after a publish or discard), leave diff
  // mode so the next edit lands in the editor instead of the read-only diff.
  if (!showDiffToggle && tab !== "edit") {
    setTab("edit");
  }
  const isDiffMode = showDiffToggle && tab === "diff";

  return (
    <div className="space-y-2">
      <div className="flex min-h-8 items-center justify-between">
        <Label size={Size.SMALL}>
          <span className="flex items-center gap-1.5">
            Scope
            {hasUnpublishedChanges && (
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            )}
          </span>
        </Label>
        {showDiffToggle ? (
          <Button
            variant={ButtonVariant.Tertiary}
            size={Size.MEDIUM}
            className={twMerge("cursor-pointer pr-0", linkStyle())}
            onClick={() => setTab(isDiffMode ? "edit" : "diff")}
          >
            {isDiffMode ? "Edit" : "See changes"}
          </Button>
        ) : null}
      </div>
      {isDiffMode ? (
        <FieldDiffView
          className="min-h-[240px] overflow-y-auto"
          savedValue={liveDescription ?? ""}
          editedValue={description ?? ""}
        />
      ) : (
        <Textarea
          placeholder={PLACEHOLDER}
          value={text}
          onChange={handleTextChange}
          height="240px"
          maxLength={PLAYBOOK_SCOPE_DESCRIPTION_MAX_LENGTH}
          isDisabled={isReadOnly}
          description={DESCRIPTION}
        />
      )}
    </div>
  );
};
